/**
 * Composer attachments: images (vision) + text files injected into the prompt.
 */

import type { MessageAttachment } from './types';

export const MAX_IMAGE_BYTES = 4 * 1024 * 1024; // 4 MB per image
export const MAX_TEXT_FILE_BYTES = 512 * 1024; // 512 KB
export const MAX_ATTACHMENTS = 8;
export const MAX_TEXT_CHARS_INJECT = 80_000;

const IMAGE_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
]);

const TEXT_TYPES = new Set([
  'text/plain',
  'text/markdown',
  'text/csv',
  'text/html',
  'text/css',
  'text/javascript',
  'application/json',
  'application/xml',
  'text/xml',
  'application/x-yaml',
  'text/yaml',
  'text/x-python',
  'text/x-typescript',
  'text/x-go',
  'text/x-rust',
  'text/x-java',
  'text/x-c',
  'text/x-c++',
  'text/x-shellscript',
]);

const TEXT_EXT = new Set([
  'txt',
  'md',
  'markdown',
  'csv',
  'json',
  'jsonl',
  'xml',
  'html',
  'htm',
  'css',
  'js',
  'jsx',
  'ts',
  'tsx',
  'mjs',
  'cjs',
  'py',
  'rb',
  'go',
  'rs',
  'java',
  'c',
  'h',
  'cpp',
  'hpp',
  'cs',
  'php',
  'sh',
  'bash',
  'zsh',
  'yml',
  'yaml',
  'toml',
  'ini',
  'env',
  'sql',
  'graphql',
  'svg',
  'log',
  'conf',
  'cfg',
  'rsx',
  'vue',
  'svelte',
  'astro',
]);

function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}

export function isImageMime(mime: string): boolean {
  return IMAGE_TYPES.has(mime.toLowerCase()) || mime.toLowerCase().startsWith('image/');
}

export function isTextLikeFile(file: File): boolean {
  const mime = (file.type || '').toLowerCase();
  if (TEXT_TYPES.has(mime)) return true;
  if (mime.startsWith('text/')) return true;
  return TEXT_EXT.has(extOf(file.name || ''));
}

function readAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function readAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

export type AttachmentResult =
  | { ok: true; attachment: MessageAttachment }
  | { ok: false; error: string };

export async function fileToAttachment(file: File): Promise<AttachmentResult> {
  const mime = file.type || 'application/octet-stream';
  const name = file.name || 'attachment';

  if (isImageMime(mime) || /\.(png|jpe?g|gif|webp)$/i.test(name)) {
    if (file.size > MAX_IMAGE_BYTES) {
      return {
        ok: false,
        error: `Image “${name}” is too large (max ${MAX_IMAGE_BYTES / 1024 / 1024} MB).`,
      };
    }
    const data = await readAsDataURL(file);
    return {
      ok: true,
      attachment: {
        id: crypto.randomUUID(),
        mimeType: mime.startsWith('image/') ? mime : 'image/png',
        name,
        data,
        size: file.size,
        kind: 'image',
      },
    };
  }

  if (isTextLikeFile(file)) {
    if (file.size > MAX_TEXT_FILE_BYTES) {
      return {
        ok: false,
        error: `File “${name}” is too large (max ${MAX_TEXT_FILE_BYTES / 1024} KB for text).`,
      };
    }
    let text = await readAsText(file);
    if (text.length > MAX_TEXT_CHARS_INJECT) {
      text =
        text.slice(0, MAX_TEXT_CHARS_INJECT) +
        `\n\n… [truncated at ${MAX_TEXT_CHARS_INJECT.toLocaleString()} characters]`;
    }
    return {
      ok: true,
      attachment: {
        id: crypto.randomUUID(),
        mimeType: mime.startsWith('text/') || mime.includes('json') ? mime : 'text/plain',
        name,
        data: text,
        size: file.size,
        kind: 'text',
      },
    };
  }

  return {
    ok: false,
    error: `“${name}” isn’t a supported type. Attach images or text/code files.`,
  };
}

/** Clipboard paste: prefer image items, else files. */
export async function attachmentsFromClipboardEvent(
  e: ClipboardEvent,
): Promise<AttachmentResult[]> {
  const items = e.clipboardData?.items;
  if (!items?.length) return [];

  const results: AttachmentResult[] = [];
  const files: File[] = [];

  for (const item of Array.from(items)) {
    if (item.kind === 'file') {
      const f = item.getAsFile();
      if (f) files.push(f);
    }
  }

  for (const f of files) {
    results.push(await fileToAttachment(f));
  }
  return results;
}

export async function attachmentsFromFileList(
  list: FileList | File[],
): Promise<AttachmentResult[]> {
  const files = Array.from(list);
  const out: AttachmentResult[] = [];
  for (const f of files) {
    out.push(await fileToAttachment(f));
  }
  return out;
}

export function mergeAttachments(
  existing: MessageAttachment[],
  incoming: MessageAttachment[],
): { next: MessageAttachment[]; error?: string } {
  const next = [...existing];
  for (const a of incoming) {
    if (next.length >= MAX_ATTACHMENTS) {
      return {
        next,
        error: `At most ${MAX_ATTACHMENTS} attachments per message.`,
      };
    }
    next.push(a);
  }
  return { next };
}

export function hasImageAttachments(atts: MessageAttachment[] | undefined): boolean {
  return (atts ?? []).some((a) => a.kind === 'image');
}

/** Short label for titles / search. */
export function attachmentSummary(atts: MessageAttachment[] | undefined): string {
  if (!atts?.length) return '';
  const images = atts.filter((a) => a.kind === 'image').length;
  const files = atts.length - images;
  const parts: string[] = [];
  if (images) parts.push(`${images} image${images === 1 ? '' : 's'}`);
  if (files) parts.push(`${files} file${files === 1 ? '' : 's'}`);
  return parts.join(', ');
}
